import { useState } from 'react';
import { Plus, Pencil, RefreshCw, Power, AlertTriangle, CheckCircle2 } from 'lucide-react';

function fmtMoney(amount, currency) {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function fmtAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const h = 36e5, d = 24 * h;
  if (diff < h) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
  if (diff < d) return `${Math.floor(diff / h)}h ago`;
  return `${Math.floor(diff / d)}d ago`;
}

export default function PackagesTable({ packages, onAdd, onEdit, onToggleActive, onRefresh }) {
  const [refreshingId, setRefreshingId] = useState(null);
  const handleRefresh = async (id) => {
    setRefreshingId(id);
    try { await onRefresh(id); } finally { setRefreshingId(null); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="packages-table">
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/40">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 leading-tight">Stripe packages</h3>
          <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
            {packages.length} package{packages.length === 1 ? '' : 's'} mapped — only active packages appear on the Buy Now panel
          </p>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-package-button"
          className="h-8 px-3 text-[12px] font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md inline-flex items-center gap-1.5 shadow-sm transition-colors"
        >
          <Plus size={12} /> Add package
        </button>
      </header>

      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase tracking-wider font-medium text-slate-400 bg-slate-50/30">
          <tr>
            <th className="px-4 py-2 text-left">Order</th>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Stripe price ID</th>
            <th className="px-4 py-2 text-right">Amount</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Last refreshed</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {packages.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No packages mapped yet. Click "Add package" to create one.</td></tr>
          ) : packages.map(p => (
            <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`pkg-row-${p.id}`}>
              <td className="px-4 py-2.5 text-slate-500 tabular-nums">{p.display_order}</td>
              <td className="px-4 py-2.5">
                <div className="text-slate-900 font-medium leading-tight">{p.package_name}</div>
                {p.description && <div className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2 max-w-md">{p.description}</div>}
              </td>
              <td className="px-4 py-2.5">
                <code className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{p.stripe_price_id}</code>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">
                {p.stripe_ok ? fmtMoney(p.amount, p.currency) : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1 flex-wrap">
                  {p.is_active ? (
                    <span className="ops-pill bg-emerald-50 text-emerald-700 border-emerald-200 px-1.5">Active</span>
                  ) : (
                    <span className="ops-pill bg-slate-50 text-slate-500 border-slate-200 px-1.5">Inactive</span>
                  )}
                  {p.stripe_ok === false && (
                    <span title={p.stripe_error || 'Cannot reach Stripe for this Price ID.'} className="ops-pill bg-amber-50 text-amber-700 border-amber-200 px-1.5 inline-flex items-center gap-1">
                      <AlertTriangle size={10} /> {p.stripe_error && p.stripe_error.startsWith('parse:') ? 'Parse error' : 'Stripe unreachable'}
                    </span>
                  )}
                  {p.stripe_ok === true && (
                    <span title="Verified with Stripe" className="text-emerald-600">
                      <CheckCircle2 size={11} />
                    </span>
                  )}
                </span>
              </td>
              <td className="px-4 py-2.5 text-slate-500">{fmtAgo(p.last_refreshed_at)}</td>
              <td className="px-4 py-2.5">
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleRefresh(p.id)}
                    disabled={refreshingId === p.id}
                    data-testid={`pkg-refresh-${p.id}`}
                    title="Refresh from Stripe"
                    className="h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={refreshingId === p.id ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={() => onToggleActive(p)}
                    data-testid={`pkg-toggle-${p.id}`}
                    title={p.is_active ? 'Deactivate (hide from Buy Now)' : 'Activate'}
                    className="h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    <Power size={12} />
                  </button>
                  <button
                    onClick={() => onEdit(p)}
                    data-testid={`pkg-edit-${p.id}`}
                    className="h-7 px-2 text-[11px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md inline-flex items-center gap-1 transition-colors"
                  >
                    <Pencil size={11} /> Edit
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
